import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Download, ScrollText, X, ChevronDown, ChevronRight, RefreshCw,
} from 'lucide-react';
import organizationService from '../../services/organization.service';
import {
  Alert,
  Badge,
  Button,
  Card,
  DateRangeFilter,
  EmptyState,
  FilterPill,
  Input,
  PageHeader,
  Select,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../ui';

const PAGE_SIZE = 50;
const ACTOR_ID_DEBOUNCE_MS = 300;
const EXPORT_ROW_CAP = 5000;
const EXPORT_PAGE_SIZE = 200;

const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

const fmtRelative = (v) => {
  if (!v) return '—';
  const diff = Math.floor((Date.now() - new Date(v)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const actionTone = (action) => {
  if (!action) return 'neutral';
  if (action.includes('created') || action.includes('provisioned')) return 'success';
  if (action.includes('deactivated') || action.includes('removed')) return 'danger';
  if (action.includes('suspended')) return 'warning';
  if (action.includes('updated')) return 'info';
  return 'neutral';
};

const outcomeTone = (o) =>
  o === 'SUCCESS' ? 'success' : o === 'FAILURE' ? 'danger' : o === 'REJECTED' ? 'warning' : 'neutral';

// Rolling window (not calendar day) to match the DateRangeFilter presets;
// converted to an absolute `dateFrom` so filtering happens server-side.
const DATE_RANGE_DAYS = { today: 1, '7d': 7, '30d': 30, '90d': 90 };

const dateRangeToParams = (range) => {
  const days = DATE_RANGE_DAYS[range];
  if (!days) return {};
  return { dateFrom: new Date(Date.now() - days * 86400000).toISOString() };
};

function getActionColor(action) {
  if (!action) return 'bg-ink-300';
  if (action.startsWith('auth.')) return 'bg-blue-500';
  if (action.startsWith('customer.')) return 'bg-purple-500';
  if (action.startsWith('membership.')) return 'bg-teal-500';
  if (action.startsWith('oauth_client.')) return 'bg-indigo-500';
  if (action.startsWith('organization.')) return 'bg-ink-400';
  if (action.startsWith('module.')) return 'bg-amber-500';
  return 'bg-ink-300';
}

function ActorTypeBadge({ actorType }) {
  if (actorType === 'CLIENT') {
    return (
      <span className="inline-block bg-purple-50 text-purple-700 rounded text-2xs font-mono px-1 mr-1.5">
        M2M
      </span>
    );
  }
  if (actorType === 'USER') {
    return (
      <span className="inline-block bg-ink-100 text-ink-600 rounded text-2xs px-1 mr-1.5">
        User
      </span>
    );
  }
  if (actorType === 'SYSTEM') {
    return (
      <span className="inline-block bg-amber-50 text-amber-700 rounded text-2xs px-1 mr-1.5">
        System
      </span>
    );
  }
  return null;
}

function ActorCell({ actorType, actorId, actorLabel }) {
  const primary = actorLabel || actorId || 'System';
  const showSecondaryId = Boolean(actorId) && actorId !== primary;
  return (
    <div className="max-w-[13rem]">
      <div className="truncate text-ink-700">
        <ActorTypeBadge actorType={actorType} />
        {primary}
      </div>
      {showSecondaryId && (
        <div className="truncate text-2xs font-mono text-ink-400" title={actorId}>
          {actorId}
        </div>
      )}
    </div>
  );
}

// Only resource types with a real destination in this portal are linked —
// there is no per-record detail route for any resource yet, so we link to
// the owning list page rather than inventing a detail page.
const RESOURCE_LINK_ROUTES = {
  oauth_client: '/api-clients',
  org_membership: '/team',
  organization: '/settings',
};

function ResourceIdLink({ resourceType, resourceId, className = '' }) {
  if (!resourceId) return null;
  const route = RESOURCE_LINK_ROUTES[resourceType];
  if (!route) {
    return <span className={className}>{resourceId}</span>;
  }
  return (
    <Link
      to={route}
      onClick={(e) => e.stopPropagation()}
      className={`${className} text-accent-700 hover:text-accent-800 hover:underline`}
    >
      {resourceId}
    </Link>
  );
}

const HIGHLIGHT_META_KEYS = new Set([
  'externalReference', 'documentType', 'documentTypes', 'status', 'tokenUse',
]);

function MetadataPanel({ metadata }) {
  if (!metadata || Object.keys(metadata).length === 0) {
    return <p className="text-xs text-ink-400 italic">No additional details</p>;
  }

  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
      {Object.entries(metadata).map(([key, value]) => (
        <React.Fragment key={key}>
          <dt className={`text-2xs font-semibold ${HIGHLIGHT_META_KEYS.has(key) ? 'text-ink-700' : 'text-ink-400'} uppercase tracking-wide self-start pt-0.5`}>
            {key}
          </dt>
          <dd className="text-xs font-mono text-ink-700 break-all">
            {Array.isArray(value)
              ? value.join(', ') || '—'
              : value === null || value === undefined
              ? '—'
              : String(value)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

const RESOURCE_TYPES = [
  { value: 'organization', label: 'Organization' },
  { value: 'org_membership', label: 'Member' },
  { value: 'oauth_client', label: 'API Client' },
  { value: 'customer', label: 'Customer' },
  { value: 'customer_document', label: 'Document' },
  { value: 'auth_session', label: 'Session' },
  { value: 'oauth_token', label: 'Token' },
];

// Curated rather than derived from loaded rows — the previous approach only
// ever listed actions present in the currently loaded page, which meant the
// dropdown itself silently hid options depending on what had loaded so far.
const ACTIONS = [
  { value: 'organization.created', label: 'Organization created' },
  { value: 'organization.updated', label: 'Organization updated' },
  { value: 'organization.portal_profile_updated', label: 'Organization profile updated' },
  { value: 'module.status_changed', label: 'Module status changed' },
  { value: 'org_module.settings_updated', label: 'Module settings updated' },
  { value: 'membership.provisioned', label: 'Member provisioned' },
  { value: 'membership.updated', label: 'Member updated' },
  { value: 'invitation.created', label: 'Invitation created' },
  { value: 'oauth_client.created', label: 'API client created' },
  { value: 'oauth_client.secret_rotated', label: 'API client secret rotated' },
  { value: 'oauth_client.revoked', label: 'API client revoked' },
  { value: 'customer.provisioned', label: 'Customer provisioned' },
  { value: 'users.create', label: 'User created' },
  { value: 'auth.token.issued', label: 'Access token issued' },
  { value: 'auth.session.generated', label: 'Session generated' },
];

const DATE_RANGE_LABELS = { today: 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days' };

const buildCsv = (items) => {
  const rows = [
    ['Time', 'Action', 'Actor Type', 'Actor', 'Actor ID', 'Outcome', 'Resource Type', 'Resource ID', 'IP Address', 'Correlation ID'],
    ...items.map((e) => [
      e.createdAt || e.timestamp || '',
      e.action || e.eventType || '',
      e.actorType || '',
      e.actorLabel || '',
      e.actorId || '',
      e.outcome || 'SUCCESS',
      e.resourceType || '',
      e.resourceId || '',
      e.ipAddress || '',
      e.correlationId || '',
    ]),
  ];
  return rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
};

const AuditLog = () => {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const [actorIdInput, setActorIdInput] = useState('');
  const [actorId, setActorId] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [outcomeFilter, setOutcomeFilter] = useState('ALL');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('ALL');
  const [dateRange, setDateRange] = useState('all');

  // Debounce the actor ID text field so it doesn't fire a request per
  // keystroke; the select-based filters below apply immediately.
  useEffect(() => {
    const handle = setTimeout(() => setActorId(actorIdInput.trim()), ACTOR_ID_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [actorIdInput]);

  const buildFilterParams = useCallback(
    () => ({
      action: actionFilter !== 'ALL' ? actionFilter : undefined,
      outcome: outcomeFilter !== 'ALL' ? outcomeFilter : undefined,
      resourceType: resourceTypeFilter !== 'ALL' ? resourceTypeFilter : undefined,
      actorId: actorId || undefined,
      ...dateRangeToParams(dateRange),
    }),
    [actionFilter, outcomeFilter, resourceTypeFilter, actorId, dateRange],
  );

  // Any filter change re-queries from the beginning (server-side), rather
  // than filtering whatever happens to already be loaded in memory.
  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await organizationService.listAuditEvents({
          limit: PAGE_SIZE,
          ...buildFilterParams(),
        });
        if (!active) return;
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        setEvents(items);
        setCursor(data?.nextCursor ?? null);
        setHasMore(Boolean(data?.nextCursor));
        setTotalCount(typeof data?.totalCount === 'number' ? data.totalCount : null);
      } catch {
        if (active) setError('Failed to load audit events.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [buildFilterParams]);

  const handleLoadMore = async () => {
    if (!cursor || loadingMore) return;
    try {
      setLoadingMore(true);
      const data = await organizationService.listAuditEvents({
        limit: PAGE_SIZE,
        cursor,
        ...buildFilterParams(),
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setEvents((prev) => [...prev, ...items]);
      setCursor(data?.nextCursor ?? null);
      setHasMore(Boolean(data?.nextCursor));
    } catch {
      setError('Failed to load more events.');
    } finally {
      setLoadingMore(false);
    }
  };

  const activeFilters = [];
  if (actorIdInput) {
    activeFilters.push({
      key: 'actor', label: 'Actor ID', value: actorIdInput,
      clear: () => setActorIdInput(''),
    });
  }
  if (actionFilter !== 'ALL') {
    const label = ACTIONS.find((a) => a.value === actionFilter)?.label || actionFilter;
    activeFilters.push({ key: 'action', label: 'Action', value: label, clear: () => setActionFilter('ALL') });
  }
  if (outcomeFilter !== 'ALL') {
    activeFilters.push({ key: 'outcome', label: 'Outcome', value: outcomeFilter, clear: () => setOutcomeFilter('ALL') });
  }
  if (resourceTypeFilter !== 'ALL') {
    const label = RESOURCE_TYPES.find((r) => r.value === resourceTypeFilter)?.label || resourceTypeFilter;
    activeFilters.push({ key: 'resource', label: 'Resource', value: label, clear: () => setResourceTypeFilter('ALL') });
  }
  if (dateRange !== 'all') {
    activeFilters.push({
      key: 'date', label: 'Date', value: DATE_RANGE_LABELS[dateRange] || dateRange,
      clear: () => setDateRange('all'),
    });
  }

  const clearAll = () => {
    setActorIdInput('');
    setActionFilter('ALL');
    setOutcomeFilter('ALL');
    setResourceTypeFilter('ALL');
    setDateRange('all');
  };

  // Exports the full filtered result set (not just the currently loaded
  // page) by paging through the remaining cursor chain, capped at
  // EXPORT_ROW_CAP so a very broad filter (e.g. "all time") can't hang the
  // browser or produce an unbounded download.
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      let all = [...events];
      let nextCursor = cursor;
      let truncated = false;

      while (nextCursor && all.length < EXPORT_ROW_CAP) {
        const data = await organizationService.listAuditEvents({
          limit: EXPORT_PAGE_SIZE,
          cursor: nextCursor,
          ...buildFilterParams(),
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        if (items.length === 0) break;
        all = all.concat(items);
        nextCursor = data?.nextCursor ?? null;
      }

      if (all.length > EXPORT_ROW_CAP) {
        all = all.slice(0, EXPORT_ROW_CAP);
        truncated = true;
      } else if (nextCursor) {
        truncated = true;
      }

      const csv = buildCsv(all);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}${truncated ? `-first-${all.length}` : ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      if (truncated) {
        setError(
          `Export capped at ${EXPORT_ROW_CAP.toLocaleString()} rows — narrow your filters to export the remaining matches.`,
        );
      }
    } catch {
      setError('Failed to export audit events.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Audit log"
        breadcrumb={['Administration', 'Audit log']}
        actions={
          <Button
            variant="secondary"
            iconLeft={exporting ? undefined : Download}
            onClick={handleExport}
            disabled={events.length === 0 || exporting}
            title="Exports all events matching the current filters, up to 5,000 rows"
          >
            {exporting ? (
              <span className="flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Exporting…
              </span>
            ) : (
              'Export'
            )}
          </Button>
        }
      />

      {error && <Alert title="Couldn't load audit log" className="mb-4">{error}</Alert>}

      {/* Toolbar */}
      <Card pad={false} className="mb-px rounded-b-none">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-ink-200 flex-wrap">
          {/* Actor ID search */}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <Input
              value={actorIdInput}
              onChange={(e) => setActorIdInput(e.target.value)}
              placeholder="Search by actor ID…"
              className="pl-9"
            />
            {actorIdInput && (
              <button type="button" onClick={() => setActorIdInput('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Action */}
          <Select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="w-48">
            <option value="ALL">All actions</option>
            {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </Select>

          {/* Outcome */}
          <Select value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)} className="w-32">
            <option value="ALL">All outcomes</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILURE">Failure</option>
            <option value="REJECTED">Rejected</option>
          </Select>

          {/* Resource type */}
          <Select value={resourceTypeFilter} onChange={(e) => setResourceTypeFilter(e.target.value)} className="w-36">
            <option value="ALL">All resources</option>
            {RESOURCE_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Select>

          {/* Date range */}
          <DateRangeFilter value={dateRange} onChange={setDateRange} />

          {activeFilters.length > 0 && (
            <button type="button" onClick={clearAll} className="flex items-center gap-1 text-xs text-ink-500 hover:text-ink-700">
              <X className="h-3.5 w-3.5" />
              Clear {activeFilters.length > 1 ? `(${activeFilters.length})` : ''}
            </button>
          )}

          <span className="text-xs text-ink-500 tnum ml-auto">
            {totalCount != null
              ? `${totalCount.toLocaleString()} matching event${totalCount === 1 ? '' : 's'}`
              : `${events.length} event${events.length === 1 ? '' : 's'} loaded`}
          </span>
        </div>

        {/* Active filter pills */}
        {activeFilters.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-ink-100 flex-wrap">
            {activeFilters.map((f) => (
              <FilterPill key={f.key} label={f.label} value={f.value} onRemove={f.clear} />
            ))}
          </div>
        )}
      </Card>

      {/* Table */}
      {loading ? (
        <Card pad={false}>
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="skeleton h-4 w-32" />
                <div className="skeleton h-4 flex-1" />
                <div className="skeleton h-4 w-20" />
                <div className="skeleton h-4 w-16" />
              </div>
            ))}
          </div>
        </Card>
      ) : events.length === 0 ? (
        <Card pad={false}>
          {activeFilters.length === 0 ? (
            <EmptyState
              icon={ScrollText}
              title="No audit events yet"
              description="Configuration changes and member activity will be recorded here."
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No matches"
              description="Adjust your search or filters."
              action={<Button variant="ghost" onClick={clearAll}>Clear filters</Button>}
            />
          )}
        </Card>
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="table">
              <THead>
                <TR>
                  <TH className="w-6" />
                  <TH className="w-44">Time</TH>
                  <TH>Action</TH>
                  <TH className="w-52">Actor</TH>
                  <TH className="w-32">Outcome</TH>
                  <TH className="w-44">Resource</TH>
                </TR>
              </THead>
              <TBody>
                {events.map((e) => {
                  const action = e.action || e.eventType || '—';
                  const ts = e.createdAt || e.timestamp;
                  const isExpanded = expandedRow === e.id;
                  return (
                    <React.Fragment key={e.id}>
                      <TR
                        className="cursor-pointer"
                        onClick={() => setExpandedRow(isExpanded ? null : e.id)}
                      >
                        <TD className="w-6">
                          {isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5 text-ink-400" />
                            : <ChevronRight className="h-3.5 w-3.5 text-ink-300" />
                          }
                        </TD>
                        <TD mono className="whitespace-nowrap text-ink-600">
                          <time title={fmtDateTime(ts)} className="tnum">{fmtRelative(ts)}</time>
                        </TD>
                        <TD>
                          <span className={`w-2 h-2 rounded-full inline-block mr-2 ${getActionColor(action)}`} />
                          <Badge tone={actionTone(action)}>{action}</Badge>
                        </TD>
                        <TD>
                          <ActorCell actorType={e.actorType} actorId={e.actorId} actorLabel={e.actorLabel} />
                        </TD>
                        <TD>
                          <Badge tone={outcomeTone(e.outcome)} dot>
                            {e.outcome || 'SUCCESS'}
                          </Badge>
                        </TD>
                        <TD>
                          <span className="text-ink-600">{e.resourceType || '—'}</span>
                          {e.resourceId && (
                            <span className="ml-1.5 text-xs font-mono">
                              <ResourceIdLink
                                resourceType={e.resourceType}
                                resourceId={`#${e.resourceId.slice(-8)}`}
                                className="text-ink-400"
                              />
                            </span>
                          )}
                        </TD>
                      </TR>
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="px-6 pb-4 pt-0 bg-ink-50 border-b border-ink-100">
                            <div className="grid grid-cols-2 gap-4 mt-3">
                              <div>
                                <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 mb-2">Event details</p>
                                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                                  <dt className="text-2xs font-semibold text-ink-400 uppercase tracking-wide">ID</dt>
                                  <dd className="text-xs font-mono text-ink-700 break-all">{e.id}</dd>
                                  {e.correlationId && (
                                    <>
                                      <dt className="text-2xs font-semibold text-ink-400 uppercase tracking-wide">Correlation</dt>
                                      <dd className="text-xs font-mono text-ink-700 break-all">{e.correlationId}</dd>
                                    </>
                                  )}
                                  {e.reasonCode && (
                                    <>
                                      <dt className="text-2xs font-semibold text-ink-400 uppercase tracking-wide">Reason</dt>
                                      <dd className="text-xs font-mono text-ink-700">{e.reasonCode}</dd>
                                    </>
                                  )}
                                  {e.resourceId && (
                                    <>
                                      <dt className="text-2xs font-semibold text-ink-400 uppercase tracking-wide">Resource ID</dt>
                                      <dd className="text-xs font-mono text-ink-700 break-all">
                                        <ResourceIdLink resourceType={e.resourceType} resourceId={e.resourceId} />
                                      </dd>
                                    </>
                                  )}
                                  {e.ipAddress && (
                                    <>
                                      <dt className="text-2xs font-semibold text-ink-400 uppercase tracking-wide">IP address</dt>
                                      <dd className="text-xs font-mono text-ink-700 break-all">{e.ipAddress}</dd>
                                    </>
                                  )}
                                  {e.userAgent && (
                                    <>
                                      <dt className="text-2xs font-semibold text-ink-400 uppercase tracking-wide">User agent</dt>
                                      <dd className="text-xs font-mono text-ink-700 break-all">{e.userAgent}</dd>
                                    </>
                                  )}
                                </dl>
                              </div>
                              <div>
                                <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 mb-2">Metadata</p>
                                <MetadataPanel metadata={e.metadata} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </TBody>
            </table>
          </div>

          {hasMore && (
            <div className="flex items-center justify-center py-4">
              <Button
                variant="secondary"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : `Load more`}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLog;
